import React, { useLayoutEffect, useRef, useState } from 'react';
import { CheckOutlined, DownOutlined } from '@ant-design/icons';
import content from './content.json';
import './style.scss';

const icons = Object.fromEntries(content.assets.map(({ file }) => {
  const asset = require(`./assets/${file}`);
  return [file, asset.default || asset];
}));

function BenefitValue({ value }) {
  if (typeof value !== 'boolean') return value;
  return (
    <>
      {value && <CheckOutlined aria-hidden="true" />}
      <span className="radisson-benefits__sr-only">{value ? '享有此项礼遇' : '不包含此项礼遇'}</span>
    </>
  );
}

export default function RadissonBenefits() {
  const pageRef = useRef(null);
  const scrollRef = useRef(null);
  const [selectedTier, setSelectedTier] = useState('all');
  const [collapsedDescriptions, setCollapsedDescriptions] = useState(new Set());
  const visibleTiers = content.tiers.filter((tier) => selectedTier === 'all' || tier.id === selectedTier);
  const visibleBenefits = content.benefits.filter((benefit) => selectedTier === 'all' || benefit.values[selectedTier] !== false);

  useLayoutEffect(() => {
    const container = pageRef.current.closest('.container');
    scrollRef.current = container || document.scrollingElement || document.documentElement;
    if (!container) return;
    container.classList.add('radisson-benefits-container');
    return () => container.classList.remove('radisson-benefits-container');
  }, []);

  const selectTier = (id) => {
    setSelectedTier(id);
    // A shorter tier list should start at its first benefit, not the old scroll offset.
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  const toggleDescription = (id) => setCollapsedDescriptions((previous) => {
    const next = new Set(previous);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  return (
    <main ref={pageRef} className="radisson-benefits" lang="zh-CN">
   

      <h1 className="hotel-member-title">
        <img className="hotel-member-logo preferred-logo" src="https://staticfile.badazhou.com/20260922/46495b14cac5b233307fcff9f3211752.png" alt="" />
        <span>丽赏会-会员礼遇</span>
      </h1>

      <header className="radisson-benefits__heading"><h1>{content.heading}</h1></header>
      <div className="fs-12 c-66 mb-30 pl-30 pr-30">每个会员级别均可享受众多会员专享礼遇，值得尽情探索和享受。在此处详细了解我们条款和条件中规定的礼遇。</div>
      <div className="radisson-benefits__filters" role="group" aria-label="会员等级筛选">
        {[{ id: 'all', name: '全部' }, ...content.tiers].map((tier) => (
          <button
            type="button"
            key={tier.id}
            aria-pressed={selectedTier === tier.id}
            aria-controls="radisson-comparison"
            onClick={() => selectTier(tier.id)}
          >{tier.name}</button>
        ))}
      </div>

      <table
        id="radisson-comparison"
        className="radisson-benefits__comparison"
        aria-label="丽赏会会员礼遇比较"
        role="table"
        style={{ '--tier-columns': visibleTiers.length }}
      >
        <thead>
          <tr role="row">
            <th scope="col" className="radisson-benefits__sr-only">礼遇</th>
            {visibleTiers.map((tier) => (
              <th key={tier.id} id={`radisson-tier-${tier.id}`} scope="col" className="radisson-benefits__tier" style={{ backgroundColor: tier.color }}>
                <span className="radisson-benefits__tier-name">{tier.name}</span>
                <span className="radisson-benefits__qualification">{tier.qualification}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleBenefits.map((benefit) => {
            const expanded = !collapsedDescriptions.has(benefit.id);
            return (
              <tr key={benefit.id} role="row">
                <th id={`radisson-${benefit.id}`} scope="row" aria-labelledby={`radisson-title-${benefit.id}`} className="radisson-benefits__benefit">
                  <button
                    type="button"
                    className="radisson-benefits__benefit-toggle"
                    aria-expanded={expanded}
                    aria-controls={`radisson-description-${benefit.id}`}
                    title={expanded ? '收起权益说明' : '查看权益说明'}
                    onClick={() => toggleDescription(benefit.id)}
                  >
                    <img className="radisson-benefits__icon" src={icons[benefit.icon]} alt="" width="44" height="44" />
                    <span id={`radisson-title-${benefit.id}`}>{benefit.title}</span>
                    <DownOutlined rotate={expanded ? 180 : 0} aria-hidden="true" />
                  </button>
                  <div
                    id={`radisson-description-${benefit.id}`}
                    className="radisson-benefits__description"
                    role="region"
                    aria-label={`${benefit.title}详情`}
                    hidden={!expanded}
                  >
                    <p>{benefit.description}</p>
                    {benefit.supplement && (
                      <div className="radisson-benefits__supplement">
                        <h2>{benefit.supplement.title}</h2>
                        <p>{benefit.supplement.description}</p>
                      </div>
                    )}
                  </div>
                </th>
                {visibleTiers.map((tier) => (
                  <td
                    key={tier.id}
                    headers={`radisson-${benefit.id} radisson-tier-${tier.id}`}
                    className="radisson-benefits__value"
                    style={{ color: tier.color }}
                  ><BenefitValue value={benefit.values[tier.id]} /></td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </main>
  );
}
